import { useEffect, useMemo, useRef, useState } from 'react'

type CreativeImage = { index: number; cdn_url: string; content_type?: string }

type DeviceAsset =
  | { kind: 'video';    cdn_url: string }
  | { kind: 'images_4'; images: CreativeImage[] }

type DeviceClass = 'phone' | 'tablet' | 'laptop'

type Ad = {
  id: string
  campaign_name: string
  cdn_url: string         // R2 / CDN URL of the creative (mp4 or single image) — phone bucket fallback
  click_url?: string
  duration_secs?: number
  creative_kind?: 'video' | 'images_4' | null
  creative_images?: CreativeImage[]   // 4 images for slideshow (per CLAUDE.md §4.19)
  // Per-device asset map (§4.X). Subset of {phone, tablet, laptop}; phone
  // always present when published. tablet/laptop fall back to phone client-side.
  assets?: Partial<Record<DeviceClass, DeviceAsset>>
}

type Effective =
  | { kind: 'video';    cdn_url: string }
  | { kind: 'images_4'; images: CreativeImage[] }
  | { kind: 'image';    cdn_url: string }   // legacy single-image fallback
  | null

const VIDEO_GATE_SECS    = 5      // 2026-05-05 Phase 1 Slot A — bump 2→5 per ad spec v1.0 (mandatory pre-auth view, IAB 5s minimum)
const IMAGE_GATE_SECS    = 2
const SLIDESHOW_TOTAL_MS = 4000   // 1s per image × 4 = 4s
const SLIDESHOW_TICK_MS  = 1000
// Don't redirect popup to Apple's captive endpoint — AP intercepts it
// and loops back to /guest/. Land on default WES page (allowed via raw
// IP captive-proxy whitelist + post-auth full internet). User manually
// taps blue ✓ Done at top-right of iOS popup; iOS BG probe will
// eventually auto-detect within ~30s either way.
const IOS_CAPTIVE_SUCCESS_URL = 'https://captive.epscloud.io/'

/**
 * Stable browser fingerprint (anh chốt 2026-05-09 Q4 — Phase 1 ship).
 * SHA-256 of opaque browser attrs that survive MAC randomization (iOS 14+
 * "Private Address", Android 10+ per-SSID randomization). Used by backend
 * to consolidate sibling client_mac_rewards rows at user login bind time.
 *
 * Privacy: opaque hash — disclosed in §4.18 privacy notice. No canvas/audio
 * fingerprint (legal-sensitive). Returns null if SubtleCrypto unavailable.
 */
async function computeDeviceFingerprint(): Promise<string | null> {
  try {
    const parts = [
      navigator.userAgent || '',
      navigator.language || '',
      (navigator as any).platform || '',
      `${screen.width}x${screen.height}`,
      String(screen.colorDepth || 0),
      String(new Date().getTimezoneOffset()),
      String(navigator.hardwareConcurrency || 0),
      String((navigator as any).deviceMemory || 0),
      Intl.DateTimeFormat().resolvedOptions().timeZone || '',
    ].join('|')
    const buf = new TextEncoder().encode(parts)
    const hash = await crypto.subtle.digest('SHA-256', buf)
    return Array.from(new Uint8Array(hash))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('')
  } catch {
    return null
  }
}

/**
 * Detect viewer device class.
 *   width  ≤ 600  → phone
 *   width  ≤ 1024 → tablet
 *   else          → laptop
 * Touch UA hint promotes a borderline 768-1024 viewport from laptop → tablet
 * (e.g. iPad Pro landscape often sits at 1024 but is still touch-first).
 */
function detectDeviceClass(): DeviceClass {
  if (typeof window === 'undefined') return 'phone'
  const w = window.innerWidth || 360
  const isTouch = (typeof navigator !== 'undefined') &&
    ((navigator.maxTouchPoints ?? 0) > 1 || /android|iphone|ipad|ipod/i.test(navigator.userAgent || ''))
  if (w <= 600) return 'phone'
  if (w <= 1024) return 'tablet'
  if (isTouch && w <= 1280) return 'tablet'
  return 'laptop'
}

/**
 * Resolve which asset bucket to render for `device`, with fallback to phone
 * (per §4.X — phone is REQUIRED, tablet/laptop optional).
 *
 * Order:
 *   1. ad.assets[device] if state-hot present
 *   2. ad.assets.phone if present
 *   3. legacy fields: ad.creative_kind=images_4 + creative_images
 *   4. legacy single video/image cdn_url
 */
function resolveEffective(ad: Ad, device: DeviceClass): Effective {
  const fromBucket = (a?: DeviceAsset): Effective => {
    if (!a) return null
    if (a.kind === 'images_4' && Array.isArray(a.images) && a.images.length === 4) {
      return { kind: 'images_4', images: a.images }
    }
    if (a.kind === 'video' && a.cdn_url) {
      return { kind: 'video', cdn_url: a.cdn_url }
    }
    return null
  }
  const tryBucket = fromBucket(ad.assets?.[device]) || fromBucket(ad.assets?.phone)
  if (tryBucket) return tryBucket
  // Legacy fallbacks (campaigns created before per-device upload)
  if (ad.creative_kind === 'images_4' && Array.isArray(ad.creative_images) && ad.creative_images.length === 4) {
    return { kind: 'images_4', images: ad.creative_images }
  }
  if (ad.cdn_url?.endsWith('.mp4')) return { kind: 'video', cdn_url: ad.cdn_url }
  if (ad.cdn_url) return { kind: 'image', cdn_url: ad.cdn_url }
  return null
}

/**
 * Portal landing page.
 *
 * Layout is responsive (mobile-first) — see styles.css for the breakpoints.
 * Connect button is gated until the user has either:
 *   • watched a video creative for ≥5s, OR
 *   • viewed an image creative for ≥5s.
 * No-ad fallback bypasses the gate.
 *
 * Ad slot scales via aspect-ratio + object-fit:cover — vertical phone
 * creatives are letterboxed but visible; landscape creatives fill the slot.
 */
export function App() {
  const [ad, setAd] = useState<Ad | null>(null)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState<string | null>(null)
  const [unlocked, setUnlocked] = useState(false)
  const [progressPct, setProgressPct] = useState(0)
  const [videoErr, setVideoErr] = useState(false)
  const [slideIdx, setSlideIdx] = useState(0)        // current image in 4-slot slideshow
  const [device, setDevice] = useState<DeviceClass>(() => detectDeviceClass())
  const [connecting, setConnecting] = useState(false)
  const [connected, setConnected] = useState(false)
  const [authError, setAuthError] = useState<string>('')
  const [bridgeRemaining, setBridgeRemaining] = useState(0)
  const [continueUrl, setContinueUrl] = useState<string>('https://captive.epscloud.io/')
  void setContinueUrl  // reserved for future bridge flow
  const [preAuthDone, setPreAuthDone] = useState(false)
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const imageTimerRef = useRef<number | null>(null)

  // Re-evaluate device class on viewport resize (rotation, window snap, etc.)
  useEffect(() => {
    const onResize = () => setDevice(detectDeviceClass())
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  // Resolve which asset bucket to render given current device + ad payload.
  // Memoised so the playback gate effect only re-fires when the effective
  // creative actually changes (not on every render).
  const effective: Effective = useMemo(
    () => (ad ? resolveEffective(ad, device) : null),
    [ad, device]
  )

  // ── Pre-authorize on mount ──────────────────────────────────────
  // Fire authorize POST immediately when React mounts (during ad gate),
  // not when user clicks Connect. By the time user clicks (2s later),
  // backend has had 2s + retry budget to authorize via UniFi controller +
  // wait for AP firmware sync. Click Connect → instant success → iOS CNA
  // BG probe succeeds next cycle → popup auto-Done much faster.
  // REVENUE NOTE: impression still gated by user clicking Connect; we
  // record impression in finalize callback (not in pre-authorize).
  useEffect(() => {
    const qs = new URLSearchParams(window.location.search)
    const ec = qs.get('ec') || ''
    const ap = qs.get('ap') || ''
    const id = qs.get('id') || ''
    if (!ec && !ap) {
      setPreAuthDone(true)  // not a captive context, skip pre-auth gate
      return
    }
    fetch('/__api/portal/pre-authorize', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ec, ap, id }),
    })
      .then((r) => r.json().catch(() => ({})))
      .then((j) => {
        // Mark done regardless of result — Connect button enables either way;
        // backend retries on click anyway. But if pre-auth succeeded, click
        // hits idempotent fast-path = instant.
        setPreAuthDone(true)
        if (j?.ok) console.log('[wes] pre-auth completed:', j?.mac)
      })
      .catch(() => setPreAuthDone(true))
  }, [])

  // ── Fetch ad once ───────────────────────────────────────────────
  // Forward ?ad_b64=… (and any other targeting params) from the portal URL
  // to /api/ads. This is how the agency Colorbox preview shows an unsaved
  // campaign without needing a KV publish first.
  useEffect(() => {
    const portalQS = window.location.search || ''
    fetch('/api/ads' + portalQS, { credentials: 'include' })
      .then(async (r) => {
        if (!r.ok) throw new Error(`ads_status_${r.status}`)
        const j = await r.json()
        setAd(j.ad ?? null)
      })
      .catch((e) => setErr(String(e)))
      .finally(() => setLoading(false))
  }, [])

  // ── Unlock gate: ad must actually be CONSUMED before impression counts.
  //   No campaign       → unlock immediately, no impression.
  //   Video             → onTimeUpdate gate, NO fallback (CLAUDE.md §4.1 §4.5).
  //   Slideshow (4 img) → 6s total, 1.5s per image, slideIdx rotates.
  //   Single image      → 5s display timer.
  // For video that fails to load OR slideshow images that fail, button stays
  // locked — no impression, no revenue (correct per §4.1 accounting).
  useEffect(() => {
    if (!ad || !effective) {
      setUnlocked(true)
      setProgressPct(100)
      return
    }
    setUnlocked(false)
    setProgressPct(0)
    setSlideIdx(0)
    setVideoErr(false)
    if (effective.kind === 'video') {
      // Video — gated by <video onTimeUpdate>. No timer fallback.
      return
    }
    const isSlideshow = effective.kind === 'images_4'
    const total = isSlideshow ? SLIDESHOW_TOTAL_MS : IMAGE_GATE_SECS * 1000
    const startedAt = performance.now()
    if (imageTimerRef.current) cancelAnimationFrame(imageTimerRef.current)
    const tick = (now: number) => {
      const elapsed = Math.max(0, now - startedAt)
      const pct = Math.min(100, (elapsed / total) * 100)
      setProgressPct(pct)
      if (isSlideshow) {
        const idx = Math.min(3, Math.floor(elapsed / SLIDESHOW_TICK_MS))
        setSlideIdx(idx)
      }
      if (elapsed >= total) {
        imageTimerRef.current = null
        setUnlocked(true)
        setProgressPct(100)
        return
      }
      imageTimerRef.current = requestAnimationFrame(tick)
    }
    imageTimerRef.current = requestAnimationFrame(tick)
    return () => {
      if (imageTimerRef.current) cancelAnimationFrame(imageTimerRef.current)
      imageTimerRef.current = null
    }
  }, [ad, effective])

  // Smooth video progress — gate on WALL CLOCK time since video first
  // started playing (not v.currentTime which jumps when iOS catches up
  // from buffer). Wall clock ensures linear ramp 0→100% over GATE_SECS
  // regardless of buffering hiccups.
  useEffect(() => {
    if (effective?.kind !== 'video' || unlocked) return
    let raf = 0
    let startedAt = 0
    const tick = () => {
      const v = videoRef.current
      if (!v) { raf = requestAnimationFrame(tick); return }
      // Wait until video actually plays (currentTime > 0) before starting clock.
      if (!startedAt) {
        if (v.currentTime > 0) startedAt = performance.now()
        raf = requestAnimationFrame(tick)
        return
      }
      const elapsedMs = performance.now() - startedAt
      const totalMs   = VIDEO_GATE_SECS * 1000
      const pct       = Math.min(100, (elapsedMs / totalMs) * 100)
      if (elapsedMs >= totalMs) {
        setProgressPct(100)
        setUnlocked(true)
        return
      }
      setProgressPct(pct)
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => { if (raf) cancelAnimationFrame(raf) }
  }, [effective, unlocked])

  // Keep timeupdate handler as no-op fallback (iOS edge cases) so the
  // <video onTimeUpdate> attribute doesn't error if removed.
  function onVideoTimeUpdate() {}

  useEffect(() => {
    if (!connected || bridgeRemaining <= 0) return
    const t = window.setTimeout(() => setBridgeRemaining((v) => Math.max(0, v - 1)), 1000)
    return () => window.clearTimeout(t)
  }, [connected, bridgeRemaining])

  useEffect(() => {
    if (!connected || bridgeRemaining !== 0) return
    const t = window.setTimeout(() => {
      window.location.replace(continueUrl || IOS_CAPTIVE_SUCCESS_URL)
    }, 300)
    return () => window.clearTimeout(t)
  }, [connected, bridgeRemaining, continueUrl])

  const gateSecs = effective?.kind === 'video'
    ? VIDEO_GATE_SECS
    : effective?.kind === 'images_4'
      ? Math.ceil(SLIDESHOW_TOTAL_MS / 1000)
      : IMAGE_GATE_SECS
  const remainingSecs = Math.max(0, Math.ceil(((100 - progressPct) / 100) * gateSecs))

  if (connected) {
    return (
      <main className="wp-shell">
        <header className="wp-header">
          <img className="wp-logo" src="/wes-logo.png" alt="WES Token logo" width="256" height="256" />
          <div className="wp-header-text">
            <h1 className="wp-h1">WES WiFi Ecosystem</h1>
            <p className="wp-tagline">Connected</p>
          </div>
        </header>
        <section className="wp-connected" aria-live="polite">
          <h2>WiFi connected</h2>
          <p>{bridgeRemaining > 0 ? `Finishing in ${bridgeRemaining}s` : 'Closing WiFi window...'}</p>
        </section>
        <section className="wp-cta-row">
          <button
            type="button"
            className={`wp-cta ${bridgeRemaining <= 0 ? 'is-unlocked' : ''}`}
            disabled={bridgeRemaining > 0}
            onClick={() => { window.location.replace(IOS_CAPTIVE_SUCCESS_URL) }}
          >
            <span className="wp-cta-label">{bridgeRemaining > 0 ? 'Finishing...' : 'Done'}</span>
          </button>
        </section>
      </main>
    )
  }

  return (
    <main className="wp-shell">
      <header className="wp-header">
        <img className="wp-logo" src="/wes-logo.png" alt="WES Token logo" width="256" height="256" />
        <div className="wp-header-text">
          <h1 className="wp-h1">WES WiFi Ecosystem</h1>
          <p className="wp-tagline">
            Watch to Earn <strong>WES</strong>, claim with your wallet
          </p>
        </div>
      </header>

      <section id="ad-slot" className={`wp-slot wp-slot--${device}`} data-device={device}>
        {loading && <span className="wp-slot-state">Loading creative…</span>}
        {!loading && err && <span className="wp-slot-state is-error">Ad unavailable ({err})</span>}
        {!loading && !err && !ad && <span className="wp-slot-state">No active campaigns right now.</span>}
        {!loading && ad && !effective && (
          <span className="wp-slot-state is-error">
            Ad cannot load — please reconnect later.
          </span>
        )}
        {!loading && ad && effective && effective.kind === 'video' && (
          videoErr ? (
            <span className="wp-slot-state is-error">
              Ad cannot load — please reconnect later.
            </span>
          ) : (
            <video
              ref={videoRef}
              src={effective.cdn_url}
              autoPlay
              muted
              loop
              playsInline
              onTimeUpdate={onVideoTimeUpdate}
              onError={() => setVideoErr(true)}
            />
          )
        )}
        {!loading && ad && effective && effective.kind === 'images_4' && (
          (() => {
            const imgs = effective.images
            const cur  = imgs[slideIdx] || imgs[0]
            return cur ? (
              <img src={cur.cdn_url} alt={`${ad.campaign_name} ${slideIdx+1}/4`} />
            ) : (
              <span className="wp-slot-state is-error">
                Ad images not available — please reconnect later.
              </span>
            )
          })()
        )}
        {!loading && ad && effective && effective.kind === 'image' && (
          <img src={effective.cdn_url} alt={ad.campaign_name} />
        )}
      </section>

      <section className="wp-cta-row">
        {ad && (
          <div className="wp-progress-mini" aria-hidden="true">
            <span style={{ transform: `scaleX(${progressPct / 100})` }} />
          </div>
        )}
        <button
          type="button"
          className={`wp-cta ${unlocked && preAuthDone ? 'is-unlocked' : ''}`}
          disabled={!unlocked || connecting || !preAuthDone}
          onClick={() => {
            if (!unlocked || connecting || !preAuthDone) return
            setConnecting(true)
            const qs = new URLSearchParams(window.location.search)
            const ec = qs.get('ec') || ''
            const ap = qs.get('ap') || ''
            const id = qs.get('id') || ''
            const landing = ad?.click_url || 'https://epscloud.io/'

            // MikroTik venue (top-level redirect from /flash/hotspot/login.html
            // injects ?link-login-only=&link-orig= into URL). Submit hidden
            // form directly to MikroTik /login with shared creds so RouterOS
            // authorizes MAC + 302 → $(link-orig) (Apple captive endpoint).
            //
            /**
             * MikroTik Hotspot login submit — captive-portal-only legacy path.
             *
             * `wes-guest` / `wes-shared` is a SHARED hotspot user defined in the
             * WES MikroTik bootstrap template
             *   Script/mikrotik/templates/wes-mikrotik-base.rsc.tmpl ~line 242
             *   /ip/hotspot/user/add name=wes-guest password=wes-shared
             *
             * This credential is RouterOS-only, NOT a WES identity. It must NOT be:
             *   • reused for /api/* backend authentication
             *   • used to derive any WES JWT or session token
             *   • logged with PII (it's a fleet-wide shared cred — low-sensitivity
             *     but visible to any captive viewer via this submit)
             *
             * Per CLAUDE.md §4.7 captive-flow architecture (anh chốt 2026-05-04
             * + audit P1.5 review 2026-05-06).
             */
            const linkLoginOnly = qs.get('link-login-only') || ''
            const linkOrig = qs.get('link-orig') || ''
            if (linkLoginOnly) {
              const form = document.createElement('form')
              form.method = 'POST'
              form.action = linkLoginOnly
              form.style.display = 'none'
              const fields: Record<string, string> = {
                username: 'wes-guest',   // RouterOS shared cred — see JSDoc above
                password: 'wes-shared',  // NOT a WES identity, NOT for /api/* auth
                dst: linkOrig || landing,
                popup: 'true',
              }
              for (const k of Object.keys(fields)) {
                const inp = document.createElement('input')
                inp.type = 'hidden'
                inp.name = k
                inp.value = fields[k]
                form.appendChild(inp)
              }
              document.body.appendChild(form)
              form.submit()
              return
            }

            // Iframe context (legacy MikroTik iframe pattern, deprecated 2026-05-04
            // because iOS CNA sandbox blocks cross-origin HTTPS iframes — kept
            // for back-compat in case some venue stays on old login.html).
            const inIframe = (() => { try { return window.self !== window.top } catch { return true } })()
            if (inIframe) {
              try {
                window.parent.postMessage({
                  type: 'WES_AD_COMPLETE',
                  ad_id: ad?.id || null,
                  landing,
                  ts: Date.now(),
                }, '*')
              } catch (e) {
                console.warn('[wes] postMessage parent failed', e)
              }
              return
            }

            // Standalone context (UniFi-managed venue) — POST authorize to
            // wes-api → ssh-worker direct AP authorize (Tier 2, ~80-150ms).
            //
            // Phase 2.3c v2 (2026-05-06): AWAIT response to harvest Slot B
            // bundle from server, encode into URL params for
            // captive-success.html so the dynamic player engages.
            // Tier 2 SSH-direct latency makes this safe (controller sync
            // stall that triggered the v1 rollback no longer applies).
            //
            // 2026-05-09: include device_fingerprint so backend can register it
            // against client_mac_rewards row for MAC-randomization recovery later.
            // Wrapped in promise chain (parent is non-async sync click handler).
            computeDeviceFingerprint().then((__fp) =>
              fetch('/__api/portal/authorize', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ec, ap, id, click_url: ad?.click_url || null,
                                       device_fingerprint: __fp }),
              })
            )
              .then(async (r) => {
                /* Distinguish 2xx success from 4xx/5xx error so we don't
                 * navigate to captive-success on a failed authorize. Anh
                 * 2026-05-07 hit this exact trap: ssh-worker SSH key was
                 * broken → publishAuthorize timeout 10s → 502 from backend
                 * → React still navigated → iPhone showed "You're connected!"
                 * but was actually NOT in the AP firewall ipset → fell back
                 * to cellular → popup polled captive forever. */
                if (!r.ok) {
                  let detail = ''
                  try { detail = JSON.stringify(await r.json()) } catch (_) { detail = r.statusText }
                  return { __failed: true, status: r.status, detail }
                }
                return r.json()
              })
              .catch((err) => ({ __failed: true, status: 0, detail: err?.message || 'network_error' }))
              .then((resp) => {
                /* On failure: stay on captive page, log + show inline error so
                 * user can retry tap Connect. Don't navigate to a stale
                 * "You're connected" page that lies about the auth state. */
                if (resp && resp.__failed) {
                  console.error('[wes-portal] /portal/authorize failed:', resp.status, resp.detail)
                  setConnecting(false)
                  setAuthError(`Authorize failed (HTTP ${resp.status}). Tap Connect again or forget WiFi + retry.`)
                  return
                }
                const params = new URLSearchParams()
                params.set('next', landing)
                if (resp?.mac) params.set('mac', resp.mac)
                /* Phase 4 (anh chốt 2026-05-07) — captive_sessions row id.
                 * Forwarded to /portal/slot-b-event for canonical attribution
                 * (preferred over MAC-based lookup, which can race when a
                 * client roams between APs in the same venue). NULL when
                 * backend session INSERT failed; slot-b-event falls through
                 * to MAC lookup automatically. */
                if (resp?.session_id) params.set('session_id', resp.session_id)
                if (resp?.slot_b) {
                  /* Slot B v2 (anh chốt 2026-05-07) — type-aware URL params.
                   *   video:  slot_b_url + slot_b_min_secs + slot_b_max_secs
                   *   images: slot_b_type=images + slot_b_images (csv) + slot_b_total_secs
                   *   survey: deferred Phase 4
                   * Token + impression_id stay common across types. */
                  const sb = resp.slot_b
                  const sbType = sb.type || (sb.url ? 'video' : null)
                  if (sbType) params.set('slot_b_type', sbType)
                  /* House ad (no advertiser). captive-success uses this to skip
                   * every billing event — there is no impression to report. */
                  if (sb.house) params.set('slot_b_house', '1')
                  if (sb.token) params.set('slot_b_token', sb.token)

                  if (sbType === 'video' && sb.url) {
                    params.set('slot_b_url',      sb.url)
                    params.set('slot_b_min_secs', String(sb.min_secs ?? 5))
                    params.set('slot_b_max_secs', String(sb.max_secs ?? 20))
                  } else if (sbType === 'images' && Array.isArray(sb.config?.urls) && sb.config.urls.length > 0) {
                    params.set('slot_b_images',     sb.config.urls.join(','))
                    params.set('slot_b_total_secs', String(sb.config.total_secs ?? 6))
                  }
                }
                window.location.replace('/captive-success.html?' + params.toString())
              })
          }}
          aria-live="polite"
          aria-disabled={!unlocked || connecting || !preAuthDone}
        >
          <span className="wp-cta-label">
            {connecting ? 'Connecting...' : (unlocked && !preAuthDone) ? 'Almost ready...' : unlocked ? 'Connect' : (ad ? `Unlocking in ${remainingSecs}s...` : 'Connect')}
          </span>
        </button>
        {authError && (
          <div role="alert" style={{
            marginTop: 12, padding: '10px 14px', borderRadius: 8,
            background: 'rgba(220,28,28,0.12)', color: '#dc1c1c',
            fontSize: 13, lineHeight: 1.4, maxWidth: 320, textAlign: 'center',
          }}>{authError}</div>
        )}
      </section>

    </main>
  )
}
